import {Circle, Flex, Text} from '@chakra-ui/react';
import {useCountdown} from '@eksab/hooks';
import {useTranslate} from '@eksab/i18n';
import {AnimatePresence, motion} from 'framer-motion';
import Head from 'next/head';
import {useEffect} from 'react';
import {useIntl} from 'react-intl';
import {ChallengeCard} from './ChallengeCard';
import {useTriviaStore} from '../state';

export function QuizCountdown() {
  const t = useTranslate();
  const challenge = useTriviaStore((state) => state.challenge!);
  const isPaused = useTriviaStore((state) => state.isClosed);
  const startChallenge = useTriviaStore((state) => state.startChallenge);
  const countdown = useCountdown({
    to: 5,
    onCompleted: startChallenge,
    isPaused,
  });

  const {formatNumber} = useIntl();

  useEffect(() => {
    const beforeUnloadListener = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    // Confirm if user wants to leave
    window.addEventListener('beforeunload', beforeUnloadListener, {capture: true});

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener, {capture: true});
    };
  }, []);

  const isSports = challenge?.topic === 'sports';

  return (
    <Flex
      backgroundImage={isSports ? '/images/quiz-countdown-bg-sports.png' : '/images/quiz-countdown-bg-art.gif'}
      backgroundSize="100%"
      backgroundPosition="bottom"
      backgroundRepeat={challenge?.topic! === 'sports' ? 'no-repeat' : 'repeat'}
      backgroundAttachment="fixed"
      bgColor="#5AC57B"
      minH="100vh"
      px="0"
    >
      <Head>
        <title>{`${t('trivia.loading.header')} - ${t('app.trivia')} | ${t('app.name')}`}</title>
      </Head>

      <Flex flex={1} flexDir="column" align="center" gap="2" pt="90px">
        <Circle
          size="230px"
          border={isSports ? '4px' : undefined}
          borderColor={isSports ? '#4AAA68' : undefined}
          bgColor={isSports ? '#54B973' : undefined}
          mb="8"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.h1
              key={countdown.seconds}
              exit={{
                scale: 2.5,
                opacity: 0,
                position: 'absolute',
              }}
              initial={{scale: 0, opacity: 0}}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              style={{
                fontSize: '180px',
                fontWeight: '700',
                color: 'white',
                textShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
              }}
              transition={{
                ease: 'easeInOut',
                duration: 0.4,
              }}
            >
              {formatNumber(countdown.seconds)}
            </motion.h1>
          </AnimatePresence>
        </Circle>

        <Text fontWeight="800" fontSize="34px" color="white">
          {t('trivia.loading.heading')}
        </Text>

        <Text fontWeight="500" fontSize="sm" mb="6" color={challenge.topic === 'art' ? 'white' : 'black'}>
          {t('trivia.loading.subheading')}
        </Text>

        <ChallengeCard challenge={challenge} previewOnly w="full" maxW="80" />
      </Flex>
    </Flex>
  );
}
